/* eslint-disable react-hooks/rules-of-hooks */

'use client';

// Side effect: patches insertBefore/removeChild against browser page translation (FUN-5044).
import '~/utils/translation-dom-guard';
import { QueryClientProvider } from '@tanstack/react-query';
import { createReactQueryClient } from '~/modules/react-query/client';
import React, { useEffect, useMemo } from 'react';
import type { I18N_CONF } from '@funkey/locales/config';
import { PostHogProvider } from 'posthog-js/react';
import dynamic from 'next/dynamic';
import { getI18n } from '@funkey/locales';
import posthog from 'posthog-js';
import { CartCtxProvider } from '~/features/cart/context';
import { GraphQLClientProvider } from '~/modules/api';
import { NotificationsContext } from '~/features/notifications/useNotifications';
import NotificationsPortal from '~/features/notifications/NotificationsPortal';
import { NotificationDispatcher } from '~/features/notifications/NotificationDispatcher';
import { Visitor } from '~/features/visitor';
import { _POSTHOG_SHARED_CONFIG } from '~/modules/posthog/shared';
import { updatePostHogDebugState } from '~/modules/posthog/PostHogDebugState';
import { useCookie } from '~/utils/react';
import { I18nContext_Provider } from '~/features/i18n/react';
import { TRPCProvider } from '~/modules/trpc/client';
import env from '~/env';
import { Auth } from '~/modules/auth/client';
const GlobalContexts: React.FC<{
  locale: I18N_CONF.Locale;
  children: React.ReactNode;
}> = props => {
  const i18n = useMemo(() => getI18n(props.locale), [props.locale]);
  const deps = useMemo(() => ({
    notifications: new NotificationDispatcher(),
    queryClient: createReactQueryClient()
  }), []);
  return <TrackingProvider>
            <I18nContext_Provider i18n={i18n}>
                <NotificationsContext.Provider value={deps.notifications}>
                    <QueryClientProvider client={deps.queryClient}>
                        <TRPCProvider locale={i18n.locale}>
                            <GraphQLClientProvider headers={{
              'x-portal': 'WEB'
            }}>
                                <CartCtxProvider>{props.children}</CartCtxProvider>

                                <Maybe_ReactQueryDevtools />
                            </GraphQLClientProvider>
                        </TRPCProvider>
                    </QueryClientProvider>

                    <NotificationsPortal />
                </NotificationsContext.Provider>
            </I18nContext_Provider>
        </TrackingProvider>;
};
export default GlobalContexts;
const TrackingProvider: React.FC<{
  children: React.ReactNode;
}> = props => {
  if (_POSTHOG_SHARED_CONFIG.ENABLED !== true) return props.children;
  const visitorId = useCookie(Visitor.Cookie.KEY);
  const session = Auth.useSession();

  // Letting the `<PostHogProvider />` init the client via `apiKey` & `options`
  // causes this error: "`posthog` was already loaded elsewhere. This may cause issues.".
  // Thus we have to manually init via useEffect

  // eslint-disable-next-line react-hooks/rules-of-hooks
  useEffect(() => {
    posthog.init(env.NEXT_PUBLIC_POSTHOG_KEY, {
      api_host: _POSTHOG_SHARED_CONFIG.HOST,
      ui_host: _POSTHOG_SHARED_CONFIG.UI_HOST,
      person_profiles: 'always',
      capture_pageview: false,
      capture_dead_clicks: true,
      capture_pageleave: true,
      capture_performance: true,
      fetch_options: _POSTHOG_SHARED_CONFIG.FETCH_OPTIONS,
      bootstrap: visitorId.state ? {
        // This automatically identifies the client to be the same one
        // as the request to the server.
        //
        // This will refresh the feature flags to be the same as on the server
        distinctID: visitorId.state
        // We can optionally manually provide feature flags from the server here.
        // But this will make statically generating pages a lot harder.
        //
        // For now it looks like it works.
      } : undefined
    });

    // The PostHog toolbar, launched from the PostHog UI to add actions or
    // inspect a page, reads the client off `window`; a module import never
    // lands there the way the script-tag install does. Assigned here rather
    // than in init's `loaded` callback, which only fires once remote config
    // has been fetched — an ad blocker between the browser and the ingest
    // host would then leave the toolbar with nothing to attach to.
    window.posthog = posthog;

    // Tag every browser-side event (incl. `ai_search.*` + `$ai_feedback`)
    // with the deploy environment so LLM observability can filter to
    // `production` only. Server events set this explicitly via
    // `_POSTHOG_SHARED_CONFIG.ENVIRONMENT`.
    posthog.register({
      environment: _POSTHOG_SHARED_CONFIG.ENVIRONMENT
    });
    posthog.reloadFeatureFlags();
    updatePostHogDebugState();

    // Track marketing properties (e.g. when a user comes from a google advert)
    {
      const TRACKING_PARAMS = ['gclid',
      // Google Click ID
      // UTM (Urchin Tracking Module) properties
      'utm_source',
      // E.g. chatgpt
      'utm_medium', 'utm_campaign', 'utm_term', 'utm_content', 'referrer'];
      const SEARCH_PARAMS = new URLSearchParams(window.location.search);
      const trackingProperties: Record<(typeof TRACKING_PARAMS)[number], string> = {};
      for (let i = 0; i < TRACKING_PARAMS.length; i++) {
        const key = TRACKING_PARAMS[i]!;
        const queryParam = SEARCH_PARAMS.get(key);

        // On initial load (coming from an external click)
        if (queryParam) {
          trackingProperties[key] = queryParam;
          window.sessionStorage.setItem(key, queryParam);
        } else {
          const storageParam = window.sessionStorage.getItem(key);
          if (storageParam) {
            trackingProperties[key] = storageParam;
          }
        }
      }
      if (Object.keys(trackingProperties).length !== 0) {
        posthog.register(trackingProperties);
      }
    }
  }, []);
  useEffect(() => {
    if (!session.user) return;

    // NOTE: this will reset the user feature flags to the state of the first ever visit of this user
    // Currently we don't respect this on the server so the feature flags might differ.
    // We don't have any flags for our portals (requiring users to be logged in) so this isn't an issue.
    posthog.identify(session.user.email);
  }, [session.user, visitorId.state]);
  return <PostHogProvider client={posthog}>{props.children}</PostHogProvider>;
};
const Lazy_ReactQueryDevtools = dynamic(() => import('@tanstack/react-query-devtools/production').then(res => res.ReactQueryDevtools), {
  ssr: false
});
const REACT_QUERY_DEVTOOLS_ALLOWED_EMAILS = new Set(['kenji@funkey.be', 'louis@funkey.be', 'maxime@funkey.be']);
const Maybe_ReactQueryDevtools: React.FC = _props => {
  const session = Auth.useSession();
  const isAllowedUser = session.user?.email ? REACT_QUERY_DEVTOOLS_ALLOWED_EMAILS.has(session.user.email.toLowerCase()) : false;
  if (isAllowedUser !== true
  // Note we can also add a secret cookie so that we can debug in anonymous user sessions
  ) {
    return null;
  }
  return <Lazy_ReactQueryDevtools />;
};